import type { Booking, CancellationMoneyInput, PaymentMethod } from '../api/bookings';
import { formatAmount } from '../format/money';
import { paymentAmount } from './payment';

/**
 * What cancelling a booking does with the money (owner, 2026-10-15) — worded once for the phone's
 * cancel screen and the website's.
 *
 * <p>
 * <strong>The shop keeps the token by default and the counter can change it</strong>, from nothing
 * up to everything paid; the rest of the rent goes back, and so does the whole deposit. What is kept
 * is invoiced as a cancellation charge, including GST. The server holds the same rule
 * (`ChangeStageAsync`): it refuses keeping more than was paid, so this mirrors that before the button.
 * </p>
 */

/** Rent the customer has paid, net of anything already given back — the server's `paidAmount`. */
export function rentPaid(booking: Pick<Booking, 'paidAmount'>): number {
  return Math.max(0, booking.paidAmount);
}

/** The deposit with the shop, which a cancellation always gives back whole. */
export function depositToGiveBack(booking: Pick<Booking, 'deposit'>): number {
  return booking.deposit.state === 'Held' ? (booking.deposit.amount ?? 0) : 0;
}

/** The token(s) taken, capped at what is still paid — the counter's starting figure to keep. */
export function suggestedKeep(booking: Pick<Booking, 'paidAmount' | 'payments'>): number {
  const tokens = booking.payments
    .filter((payment) => payment.kind === 'Token')
    .reduce((sum, payment) => sum + payment.amount, 0);

  return Math.min(Math.max(0, tokens), rentPaid(booking));
}

export interface CancelMoney {
  /** Rent paid so far. */
  paid: number;
  /** What the shop keeps, as typed — null when the box is empty or not a number. */
  keep: number | null;
  /** Rent going back. */
  rentBack: number;
  /** Deposit going back. */
  depositBack: number;
  /** Everything handed over at the counter. */
  totalBack: number;
  /** Why the button is off, or null. */
  problem: string | null;
}

/**
 * The money a cancellation moves, from what the counter typed in the "keep" box.
 *
 * <p>
 * A blank box is a problem rather than zero: keeping nothing is a decision the clerk types as 0, not
 * one a cleared field makes for them.
 * </p>
 */
export function cancelMoney(booking: Booking, keepText: string, currency = ''): CancelMoney {
  const paid = rentPaid(booking);
  const depositBack = depositToGiveBack(booking);
  const trimmed = keepText.trim();
  const keep = trimmed === '0' ? 0 : paymentAmount(trimmed);

  const problem =
    keep === null
      ? 'Type how much the shop keeps — 0 to give everything back.'
      : keep > paid
        ? `The customer has paid ${currency}${formatAmount(paid)} towards the rent, so the shop cannot keep more than that.`
        : null;

  const rentBack = keep === null || keep > paid ? 0 : paid - keep;

  return { paid, keep, rentBack, depositBack, totalBack: rentBack + depositBack, problem };
}

/** Whether a cancellation has any money to settle — nothing paid and no deposit means no money step. */
export function cancellationMovesMoney(booking: Booking): boolean {
  return rentPaid(booking) > 0 || depositToGiveBack(booking) > 0;
}

/** The request's money part, or null while the counter's answer is not usable. */
export function cancellationMoneyInput(
  money: CancelMoney,
  refundMethod: PaymentMethod,
  refundCashAccountId: number | null = null,
): CancellationMoneyInput | null {
  return money.problem === null && money.keep !== null
    ? { keep: money.keep, refundMethod, refundCashAccountId }
    : null;
}

/** The cancel button, naming the money it hands over: "Cancel and give back ₹11,750". */
export function cancelLabel(money: CancelMoney, currency = ''): string {
  return money.totalBack > 0
    ? `Cancel and give back ${currency}${formatAmount(money.totalBack)}`
    : 'Cancel the booking';
}
